import React, { useEffect, useState } from 'react';
import { Plus, MapPin, Clock, Users, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { RouteForm } from '../components/routes/RouteForm';
import { RouteTable } from '../components/routes/RouteTable';
import { Route, CreateRouteForm, UpdateRouteForm } from '../types';
import { RouteService } from '../services/routeService';
import toast from 'react-hot-toast';

export const Routes: React.FC = () => {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState<Route | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<Date | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadRoutes();
  }, []);

  const loadRoutes = async () => {
    try {
      setLoading(true);
      const routesData = await RouteService.getRoutes(50);
      setRoutes(routesData);
      setHasNext(routesData.length === 50);
      const last = routesData[routesData.length - 1];
      setNextCursor(last ? last.createdAt : null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasNext || !nextCursor) return;
    try {
      setLoadingMore(true);
      const more = await RouteService.getRoutes(50, nextCursor);
      setRoutes(prev => [...prev, ...more]);
      setHasNext(more.length === 50);
      const last = more[more.length - 1];
      setNextCursor(last ? last.createdAt : nextCursor);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load more routes');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCreateRoute = async (data: CreateRouteForm) => {
    try {
      setFormLoading(true);
      await RouteService.createRoute(data);
      toast.success('Route created successfully');
      setShowForm(false);
      await loadRoutes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create route');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateRoute = async (data: UpdateRouteForm) => {
    if (!editingRoute) return;

    try {
      setFormLoading(true);
      await RouteService.updateRoute(editingRoute.id, data);
      toast.success('Route updated successfully');
      setEditingRoute(null);
      await loadRoutes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update route');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteRoute = async (route: Route) => {
    if (!confirm(`Are you sure you want to delete the route ${route.fromCity} → ${route.toCity}?`)) {
      return;
    }

    try {
      await RouteService.deleteRoute(route.id);
      toast.success('Route deleted successfully');
      await loadRoutes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete route');
    }
  };

  const handleEditRoute = (route: Route) => {
    setEditingRoute(route);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRoute(null);
  };

  const handleToggleStatus = async (route: Route) => {
    try {
      // Mock status toggle for now
      toast.success(`Route ${route.isActive ? 'deactivated' : 'activated'} successfully`);
      await loadRoutes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update route status');
    }
  };

  // Calculate stats
  const totalRoutes = routes.length;
  const activeRoutes = routes.filter(route => route.isActive).length;
  const averagePrice = routes.length > 0 ? routes.reduce((sum, route) => sum + route.price, 0) / routes.length : 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Routes</h1>
          <p className="text-secondary-600">Manage bus routes and schedules</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="flex items-center">
          <Plus className="h-4 w-4 mr-2" />
          Add Route
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MapPin className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Routes</p>
                <p className="text-2xl font-bold text-secondary-900">{totalRoutes}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Active Routes</p>
                <p className="text-2xl font-bold text-secondary-900">{activeRoutes}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Distance</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {Math.round(routes.reduce((sum, route) => sum + (route.distance || 0), 0))} km
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Avg. Price</p>
                <p className="text-2xl font-bold text-secondary-900">₵{averagePrice.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Routes Table */}
      <RouteTable
        routes={routes}
        onEdit={handleEditRoute}
        onDelete={handleDeleteRoute}
        onToggleStatus={handleToggleStatus}
        loading={loading}
      />

      {/* Route Form */}
      {(showForm || editingRoute) && (
        <RouteForm
          route={editingRoute || undefined}
          onSubmit={editingRoute ? (data: any) => handleUpdateRoute(data as UpdateRouteForm) : (data: any) => handleCreateRoute(data as CreateRouteForm)}
          onCancel={handleCloseForm}
          loading={formLoading}
        />
      )}

      {hasNext && (
        <div className="flex justify-center">
          <Button onClick={loadMore} loading={loadingMore} variant="outline">
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};
